import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Wordmark } from '../components/Wordmark'
import { createSignupOrganization, type AuthenticatedSession } from '../api'
import { disputeLockLabel } from '../format'
import { clearStoredSignupIntent, readStoredSignupIntent } from '../signupIntent'
import { count, formatUsd } from '../format'

/**
 * Opens the organization, then hands the person to its billing page.
 *
 * The shell shows this page for as long as the session it holds says the
 * person needs to sign up, whatever the address. Navigating alone left that
 * session in place, so the page stayed on "Creating…" after the organization
 * existed and only a reload got the person in. onCreated reads the session
 * again, and the new membership takes them to the address they were sent to.
 */
export async function openOrganization(
  name: string,
  intent: ReturnType<typeof readStoredSignupIntent>,
  then: { navigate: (path: string) => void; onCreated: () => void },
  create: typeof createSignupOrganization = createSignupOrganization,
): Promise<void> {
  const res = await create({
    name: name.trim(),
    composition: intent?.composition ?? undefined,
    utm_source: intent?.utm_source,
  })
  clearStoredSignupIntent()
  then.navigate(res.billing_path ?? `/orgs/${res.organization.id}/billing`)
  then.onCreated()
}

/**
 * Shown on every address while the person has no organization. Somebody who
 * signed in with the wrong address, or was removed from their only
 * organization, must be able to leave without opening one, as on the terms
 * page.
 */
export function Signup({ session, onCreated, onSignOut }: {
  session: AuthenticatedSession
  onCreated: () => void
  onSignOut: () => Promise<void>
}) {
  const navigate = useNavigate()
  const intent = useMemo(() => readStoredSignupIntent(), [])
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [error, setError] = useState('')

  const composition = intent?.composition
  const monthly = intent?.monthly_cents

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await openOrganization(name, intent, {
        navigate: (path) => navigate(path, { replace: true }),
        onCreated,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create your organization.')
      setBusy(false)
    }
  }

  async function leave() {
    setLeaving(true)
    setError('')
    try {
      await onSignOut()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign you out.')
    } finally {
      setLeaving(false)
    }
  }

  return (
    <main className="page page-narrow">
      <div className="card stack">
        <div className="signin-logo">
          <Wordmark />
        </div>
        <h1>Start your organization</h1>
        <p className="muted">
          Signed in as <span className="signup-address">{session.identity.email ?? session.identity.display_name}</span>. Name your workspace, then we&apos;ll
          start your trial with the plan you built{composition ? '' : ' (Team defaults)'}.
        </p>

        {composition && (
          <div className="card card-tight billing-summary">
            <div className="eyebrow">Your build</div>
            <ul className="small muted billing-included-list">
              <li>{composition.sender_seats} sender seats</li>
              <li>{count(composition.completions_per_month)} completions / month</li>
              <li>{count(composition.active_envelopes)} active documents</li>
              <li>
                Retention:{' '}
                {composition.retention_days === 2555
                  ? '7 years'
                  : composition.retention_days === 1095
                    ? '3 years'
                    : '1 year'}
              </li>
              {composition.collaboration && <li>Team collaboration</li>}
              {composition.legal_hold && (
                <li>
                  {disputeLockLabel}
                  <span className="faint"> (advanced add-on)</span>
                </li>
              )}
            </ul>
            {monthly != null && monthly > 0 && (
              <p className="billing-summary-title">{formatUsd(monthly / 100)}/mo after trial</p>
            )}
          </div>
        )}

        <form className="stack" onSubmit={(e) => void submit(e)}>
          <label htmlFor="org-name">Organization name</label>
          <input
            id="org-name"
            type="text"
            autoComplete="organization"
            required
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Contracting"
          />
          {error && <div className="notice notice-bad">{error}</div>}
          <button className="btn btn-primary btn-block btn-lg" type="submit" disabled={busy || leaving || !name.trim()}>
            {busy ? 'Creating…' : 'Start 14-day trial'}
          </button>
        </form>
        <button
          id="signup-sign-out"
          className="btn btn-ghost btn-block"
          type="button"
          disabled={busy || leaving}
          onClick={() => void leave()}
        >
          {leaving ? 'Signing out…' : 'Sign out'}
        </button>
      </div>
    </main>
  )
}
